import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaBoxOpen, FaSearch } from "react-icons/fa";
import { HiOutlineArrowRight } from "react-icons/hi";
import UserBookingRemaining from "../SharedComponents/UserBookingRemaining";
import Box from "../../../setupPage/SharedComponents/Box";
import axios from "axios";
import BookingHistoryTable from "../SharedComponents/BookingHistoryTable";
import useParcelBookingRefresh from "../../../../hooks/useParcelBookingRefresh";
import { dedupeBookingHistoryByParcel } from "../../../../utils/bookingHistory";

const UserSubRoot = () => {
  const backendUrl = process.env.REACT_APP_API_URL;
  let userId = localStorage.getItem("sundorbonBookingUserDetails");
  const localStorageObj = JSON.parse(userId);

  const [fetchedData, setFetchedData] = useState(null);
  const [bookingSummary, setBookingSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [parcelStatusData, setParcelStatusData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedParcelInfo, setSelectedParcelInfo] = useState(null);

  const displayName =
    localStorageObj?.Name ||
    localStorageObj?.Email?.split("@")[0] ||
    "there";

  const totalParcels = bookingSummary?.totalParcelCount || 0;
  const bookingCount = Array.isArray(fetchedData) ? fetchedData.length : 0;

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  const fetchBookingHistory = useCallback(() => {
    if (!localStorageObj?.Id) return;

    setLoading(true);
    axios
      .get(
        `${backendUrl}ParcelBookingInformation/GetParcelBookingHistoryByUserId/${localStorageObj.Id}`,
      )
      .then((res) => {
        setFetchedData(dedupeBookingHistoryByParcel(res.data));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err.message);
        setError(err);
        setLoading(false);
      });
  }, [backendUrl, localStorageObj?.Id]);

  const fetchBookingSummary = useCallback(() => {
    if (!localStorageObj?.Id) return;

    setSummaryLoading(true);
    axios
      .get(
        `${backendUrl}ParcelBookingInformation/GetUserBookingSummerybyUserId/${localStorageObj.Id}`,
      )
      .then((res) => {
        setBookingSummary(res.data[0]);
        setSummaryLoading(false);
      })
      .catch((err) => {
        console.error(err.message);
        setError(err);
        setSummaryLoading(false);
      });
  }, [backendUrl, localStorageObj?.Id]);

  const refreshDashboard = useCallback(() => {
    fetchBookingHistory();
    fetchBookingSummary();
  }, [fetchBookingHistory, fetchBookingSummary]);

  useEffect(() => {
    refreshDashboard();
  }, [refreshDashboard]);

  useParcelBookingRefresh((event) => {
    const detail = event?.detail;
    if (detail?.history != null) {
      setFetchedData(dedupeBookingHistoryByParcel(detail.history));
      setLoading(false);
    } else {
      fetchBookingHistory();
    }
    if (detail?.summary != null) {
      setBookingSummary(detail.summary);
      setSummaryLoading(false);
    } else {
      fetchBookingSummary();
    }
  });

  useEffect(() => {
    if (!selectedParcel) {
      setParcelStatusData(null);
      setStatusLoading(false);
      return;
    }
    setStatusLoading(true);
    axios
      .get(
        `${backendUrl}ParcelStatus/GetParcelStatusRankByParcelId?parcelId=${selectedParcel}`,
      )
      .then((res) => {
        setParcelStatusData(res.data);
        setStatusLoading(false);
      })
      .catch((err) => {
        console.error(err.message);
        setParcelStatusData(null);
        setStatusLoading(false);
      });
  }, [selectedParcel, backendUrl]);

  return (
    <Box top>
      <div className="ud-dash space-y-7 pb-10 px-1 sm:px-0">
        {/* Hero */}
        <section className="ud-hero relative overflow-hidden rounded-2xl border border-[#e0d0c4] px-5 py-6 sm:px-7 sm:py-7">
          <div className="ud-hero-glow" aria-hidden />
          <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#c2410c]">
                Sundarban Cargo · My account
              </p>
              <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-stone-900 leading-tight">
                {greeting},{" "}
                <span className="text-[#6D4C41]">{displayName}</span>
              </h1>
              <p className="mt-2 text-sm sm:text-base text-stone-600 max-w-md leading-relaxed">
                Track shipments, review payments, and open invoices for every
                parcel in one place.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-xl border border-[#e8ddd4] bg-white/80 backdrop-blur px-4 py-3 min-w-[120px]">
                <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                  Total parcels
                </p>
                <p className="text-2xl font-black tabular-nums text-stone-900">
                  {summaryLoading ? "—" : totalParcels || bookingCount}
                </p>
              </div>
              <Link
                to="/bookings"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#6D4C41] to-[#423028] px-5 py-3.5 text-sm font-bold text-white shadow-md transition hover:brightness-110"
              >
                New booking
                <HiOutlineArrowRight className="text-base" />
              </Link>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section>
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-stone-900">At a glance</h2>
              <p className="text-xs text-stone-500 mt-0.5">
                Live counts from your booking portfolio
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            <UserBookingRemaining
              id={1}
              name="Shipped"
              value={bookingSummary?.deliveryCount || 0}
              total={bookingSummary?.totalParcelCount || 0}
              loading={summaryLoading}
            />
            <UserBookingRemaining
              id={2}
              name="Pending"
              value={bookingSummary?.pendingCount || 0}
              total={bookingSummary?.totalParcelCount || 0}
              loading={summaryLoading}
            />
            <UserBookingRemaining
              id={3}
              name="Paid"
              value={bookingSummary?.paidCount || 0}
              total={bookingSummary?.totalParcelCount || 0}
              loading={summaryLoading}
            />
          </div>
        </section>

        {/* History */}
        <section className="ud-history rounded-2xl border border-[#e8ddd4] bg-white overflow-hidden shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#efe6df] bg-[#fcf8f5] px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#6D4C41] text-white shadow-sm">
                <FaBoxOpen className="text-lg" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-stone-900 leading-tight">
                  Booking history
                </h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  Expand a row for tracking, parties, and invoices
                </p>
              </div>
            </div>
            {!loading && !error && bookingCount > 0 ? (
              <span className="inline-flex self-start sm:self-auto items-center gap-1.5 rounded-lg border border-[#e8ddd4] bg-white px-3 py-1.5 text-xs font-bold text-stone-600">
                <FaSearch className="text-[10px] text-stone-400" />
                {bookingCount} booking{bookingCount === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>

          <div className="p-4 sm:p-5">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 text-stone-500">
                <div className="h-8 w-8 rounded-full border-2 border-[#6D4C41] border-t-transparent animate-spin mb-3" />
                <p className="text-sm font-medium">Loading booking history…</p>
              </div>
            ) : error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-8 text-center text-red-700 text-sm font-medium">
                Error loading booking history: {error.message}
              </div>
            ) : (
              <BookingHistoryTable
                data={fetchedData}
                onSelectParcel={(parcel) => {
                  setSelectedParcelInfo(parcel);
                  setSelectedParcel(parcel?.parcelNo ?? null);
                }}
                selectedParcel={selectedParcel}
                parcelStatusData={parcelStatusData}
                statusLoading={statusLoading}
                embedded
              />
            )}
          </div>
        </section>
      </div>
    </Box>
  );
};

export default UserSubRoot;
